import EntityNodeTree from './EntityNodeTree';
import {
    ENTITY_PART_TYPE_PLACEHOLDER,
    ENTITY_PART_TYPE_GAIN_ACCOUNT,
    ENTITY_PART_TYPE_GAIN_ACCOUNT_GROUP,
    ENTITY_PART_TYPE_CUSTOM_ACCOUNT,
} from '../models/common/EntityPart';
import {
    formatDisplayName,
    NAME_SEPARATOR,
} from 'utils/ThirdPartyCompanyFormatter';

class CompanyNodeTree extends EntityNodeTree {
    initializeNodes(placeholderCompanies, knownCompanies) {
        super.initializeNodes();
        this.initializePlaceholderCompanyNodes(placeholderCompanies);
        this.initializeKnownCompanyNodes(knownCompanies);
    }

    initializePlaceholderCompanyNodes(placeholderCompanies) {
        if (placeholderCompanies) {
            this.pushParentNode(PLACEHOLDER_COMPANY_NODE);
            const parent = this.getNode(PLACEHOLDER_COMPANY_NODE);

            const uniquePlaceholderCompanies = [
                ...new Set(placeholderCompanies),
            ].sort();

            uniquePlaceholderCompanies.forEach((company) =>
                this.pushLeafNode(
                    ENTITY_PART_TYPE_PLACEHOLDER + company,
                    company,
                    parent
                )
            );
        }
    }

    buildFormattedValue(company) {
        const formattedDisplayName = formatDisplayName(company.displayName);

        if (!formattedDisplayName) {
            return company.name.toUpperCase();
        }

        return (
            <>
                <b>{formattedDisplayName.toUpperCase()}</b>
                {NAME_SEPARATOR}
                {company.name.toUpperCase()}
            </>
        );
    }

    initializeKnownCompanyNodes(knownCompanies) {
        if (knownCompanies) {
            // Step 1: push parent nodes in sorted order
            const parentCompanies = knownCompanies.filter(
                (c) =>
                    c.type === ENTITY_PART_TYPE_GAIN_ACCOUNT_GROUP ||
                    (c.type === ENTITY_PART_TYPE_CUSTOM_ACCOUNT && !c.parentId)
            );
            this.sortCompanies(parentCompanies);
            for (const company of parentCompanies) {
                this.pushParentNode(
                    company.name,
                    this.getNodeTypePrefix(company) + company.id,
                    this.buildFormattedValue(company)
                );
            }

            // Step 2: add children sorted within each parent group
            const childrenByParentId = new Map();
            for (const company of knownCompanies) {
                if (
                    company.type === ENTITY_PART_TYPE_GAIN_ACCOUNT ||
                    (company.type === ENTITY_PART_TYPE_CUSTOM_ACCOUNT &&
                        company.parentId)
                ) {
                    const group =
                        childrenByParentId.get(company.parentId) ?? [];
                    group.push(company);
                    childrenByParentId.set(company.parentId, group);
                }
            }

            for (const children of childrenByParentId.values()) {
                this.sortCompanies(children);
                for (const company of children) {
                    this.addChildNode(
                        company,
                        this.findParentCompany(company, knownCompanies),
                        this.getNodeTypePrefix(company)
                    );
                }
            }
        }
    }

    findParentCompany(company, knownCompanies) {
        return knownCompanies.find((c) => c.id === company.parentId);
    }

    addChildNode(company, parentCompany, leafNodeType) {
        const parentId = parentCompany?.id ?? company.parentId;
        const parentNodeKey = ENTITY_PART_TYPE_GAIN_ACCOUNT_GROUP + parentId;

        let parent =
            this.getNode(parentNodeKey) ??
            this.pushParentNode(
                parentCompany?.name ?? company.parentName,
                parentNodeKey,
                parentCompany
                    ? this.buildFormattedValue(parentCompany)
                    : undefined
            );

        this.pushLeafNode(
            leafNodeType + company.id,
            company.name,
            parent,
            company.countryCode,
            this.buildFormattedValue(company)
        );
    }

    getNodeTypePrefix(company) {
        if (company.type === ENTITY_PART_TYPE_GAIN_ACCOUNT_GROUP) {
            return ENTITY_PART_TYPE_GAIN_ACCOUNT_GROUP;
        }
        if (company.type === ENTITY_PART_TYPE_GAIN_ACCOUNT) {
            return ENTITY_PART_TYPE_GAIN_ACCOUNT;
        }
        return ENTITY_PART_TYPE_CUSTOM_ACCOUNT;
    }

    sortCompanies(companies) {
        const collator = new Intl.Collator('en', { sensitivity: 'base' });
        companies.sort((a, b) => {
            const getKey = (c) =>
                (c.displayName?.trim() || c.name || '').trim();
            return collator.compare(getKey(a), getKey(b));
        });
    }
}

const PLACEHOLDER_COMPANY_NODE = 'PLACEHOLDER';

CompanyNodeTree.States = EntityNodeTree.States;

CompanyNodeTree.RootNodeKey = EntityNodeTree.RootNodeKey;
CompanyNodeTree.PlaceholderCompanyNode = PLACEHOLDER_COMPANY_NODE;
CompanyNodeTree.PlaceholderCompanyPrefix =
    EntityNodeTree.PlaceholderEntityPrefix;

export default CompanyNodeTree;
